import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert, Button, Card, Col, DatePicker, Empty, Grid, Pagination, Row,
  Segmented, Select, Space, Spin, Statistic, Table, Tag, Typography
} from 'antd'
import {
  ArrowDownOutlined, ArrowUpOutlined, BankOutlined, FileTextOutlined,
  ReloadOutlined, SwapOutlined, WalletOutlined
} from '@ant-design/icons'
import { Column } from '@ant-design/charts'
import dayjs from 'dayjs'
import contactService from '../../services/contactService'
import './ContactDashboard.css'

const { RangePicker } = DatePicker
const { Text } = Typography

const money = value => Number(value || 0).toLocaleString('th-TH', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})

const statusMap = {
  paid: { label: 'ชำระแล้ว', color: 'success' },
  approved: { label: 'อนุมัติแล้ว', color: 'success' },
  invoice: { label: 'ใบแจ้งหนี้', color: 'blue' },
  partial: { label: 'ชำระบางส่วน', color: 'orange' },
  overdue: { label: 'เกินกำหนด', color: 'error' },
  pending: { label: 'รอดำเนินการ', color: 'warning' },
  rejected: { label: 'ไม่อนุมัติ', color: 'error' },
  cancelled: { label: 'ยกเลิก', color: 'default' }
}

const presetRange = key => {
  const today = dayjs()
  if (key === 'month') return [today.startOf('month'), today.endOf('month')]
  if (key === 'last_month') {
    const lastMonth = today.subtract(1, 'month')
    return [lastMonth.startOf('month'), lastMonth.endOf('month')]
  }
  if (key === 'year') return [today.startOf('year'), today.endOf('year')]
  return null
}

export default function ContactDashboard({ contactId }) {
  const screens = Grid.useBreakpoint()
  const isMobile = !screens.md
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(null)
  const [preset, setPreset] = useState('all')
  const [range, setRange] = useState(null)
  const [transactionType, setTransactionType] = useState('all')
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState({ total: 0, limit: 20 })
  const [error, setError] = useState('')

  const loadDashboard = useCallback(async () => {
    if (!contactId) return
    setLoading(true)
    setError('')
    try {
      const result = await contactService.getContactDashboard(contactId, {
        page,
        limit: 20,
        transaction_type: transactionType,
        start_date: range?.[0]?.format('YYYY-MM-DD'),
        end_date: range?.[1]?.format('YYYY-MM-DD')
      })
      setData(result.data)
      setPagination(result.pagination || { total: 0, limit: 20 })
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'ไม่สามารถโหลด Dashboard ของรายชื่อนี้ได้')
    } finally {
      setLoading(false)
    }
  }, [contactId, page, range, transactionType])

  useEffect(() => { loadDashboard() }, [loadDashboard])

  const selectPreset = value => {
    setPreset(value)
    setPage(1)
    if (value !== 'custom') setRange(presetRange(value))
  }

  const chartData = useMemo(() => (data?.trend || []).flatMap(row => [
    { period: dayjs(`${row.period}-01`).format('MMM YY'), type: 'ยอดขาย', amount: Number(row.sales || 0) },
    { period: dayjs(`${row.period}-01`).format('MMM YY'), type: 'ยอดซื้อ', amount: Number(row.purchases || 0) }
  ]), [data?.trend])

  const chartConfig = {
    data: chartData,
    xField: 'period',
    yField: 'amount',
    colorField: 'type',
    height: isMobile ? 250 : 320,
    transform: [{ type: 'dodgeX' }],
    scale: { color: { range: ['#16a34a', '#ef4444'] } },
    axis: { y: { labelFormatter: value => Number(value).toLocaleString('th-TH') } },
    tooltip: { items: [{ channel: 'y', valueFormatter: value => `${money(value)} บาท` }] }
  }

  const summary = data?.summary || {}
  const sales = summary.sales || {}
  const purchases = summary.purchases || {}
  const transactions = data?.transactions || []

  const columns = [
    {
      title: 'วันที่', dataIndex: 'transaction_date', width: 110,
      render: value => dayjs(value).format('DD/MM/YYYY')
    },
    {
      title: 'ประเภท', dataIndex: 'transaction_type', width: 100,
      render: value => <Tag color={value === 'sale' ? 'green' : 'red'}>{value === 'sale' ? 'ขายให้' : 'ซื้อจาก'}</Tag>
    },
    {
      title: 'เลขที่เอกสาร', dataIndex: 'document_number', width: 150,
      render: (value, row) => <Button type="link" className="contact-dashboard__document-link" href={`${row.transaction_type === 'sale' ? '/receipts' : '/expenses'}?search=${encodeURIComponent(value || '')}`}>{value || '-'}</Button>
    },
    { title: 'รายละเอียด', dataIndex: 'description', ellipsis: true },
    {
      title: 'ยอดรวม', dataIndex: 'total_amount', align: 'right', width: 140,
      render: value => <Text strong>{money(value)} บาท</Text>
    },
    {
      title: 'สถานะ', dataIndex: 'status', width: 120,
      render: value => <Tag color={statusMap[value]?.color || 'default'}>{statusMap[value]?.label || value || '-'}</Tag>
    }
  ]

  const renderMobileTransaction = row => (
    <Card key={`${row.transaction_type}-${row.id}`} size="small" className={`contact-transaction-card ${row.transaction_type}`}>
      <div className="contact-transaction-card__top">
        <Space size={6} wrap>
          <Tag color={row.transaction_type === 'sale' ? 'green' : 'red'}>{row.transaction_type === 'sale' ? 'ขายให้ลูกค้า' : 'ซื้อจากผู้ขาย'}</Tag>
          <Tag color={statusMap[row.status]?.color || 'default'}>{statusMap[row.status]?.label || row.status || '-'}</Tag>
        </Space>
        <Text type="secondary">{dayjs(row.transaction_date).format('DD/MM/YY')}</Text>
      </div>
      <Button type="link" className="contact-dashboard__document-link mobile" href={`${row.transaction_type === 'sale' ? '/receipts' : '/expenses'}?search=${encodeURIComponent(row.document_number || '')}`}>{row.document_number || '-'}</Button>
      <Text className="contact-transaction-card__description">{row.description || '-'}</Text>
      <div className="contact-transaction-card__amount">{money(row.total_amount)} บาท</div>
    </Card>
  )

  return <div className="contact-dashboard">
    <Card bordered={false} className="contact-dashboard__filter">
      <div className="contact-dashboard__filter-row">
        <Segmented
          options={[
            { label: 'ทั้งหมด', value: 'all' },
            { label: 'เดือนนี้', value: 'month' },
            { label: 'เดือนก่อน', value: 'last_month' },
            { label: 'ปีนี้', value: 'year' },
            { label: 'กำหนดเอง', value: 'custom' }
          ]}
          value={preset}
          onChange={selectPreset}
        />
        <Button icon={<ReloadOutlined spin={loading} />} onClick={loadDashboard}>รีเฟรช</Button>
      </div>
      {preset === 'custom' && <RangePicker value={range} onChange={value => { setRange(value); setPage(1) }} format="DD/MM/YYYY" allowClear />}
      <Text type="secondary">{range ? `${range[0].format('DD MMM YYYY')} – ${range[1].format('DD MMM YYYY')}` : 'ข้อมูลสะสมทั้งหมดของรายชื่อนี้'}</Text>
    </Card>

    {error && <Alert type="error" showIcon message={error} />}
    {data && (!data.access?.receipts || !data.access?.expenses) && <Alert className="contact-dashboard__access" type="warning" showIcon message="ข้อมูลบางส่วนถูกซ่อนตามสิทธิ์ผู้ใช้งาน" description={`${!data.access?.receipts ? 'ไม่มีสิทธิ์ดูใบเสร็จ ' : ''}${!data.access?.expenses ? 'ไม่มีสิทธิ์ดูค่าใช้จ่าย' : ''}`} />}

    {loading && !data ? <div className="contact-dashboard__loading"><Spin size="large" /></div> : <>
      <Row gutter={[12, 12]} className="contact-dashboard__kpis">
        <Col xs={12} lg={6}><Card className="contact-kpi sale" bordered={false}><Statistic title="กิจการขายให้รายนี้" value={Number(sales.total_amount || 0)} precision={2} suffix="บาท" prefix={<ArrowUpOutlined />} /><Text type="secondary">{Number(sales.document_count || 0)} เอกสาร</Text></Card></Col>
        <Col xs={12} lg={6}><Card className="contact-kpi purchase" bordered={false}><Statistic title="กิจการซื้อจากรายนี้" value={Number(purchases.total_amount || 0)} precision={2} suffix="บาท" prefix={<ArrowDownOutlined />} /><Text type="secondary">{Number(purchases.document_count || 0)} เอกสาร</Text></Card></Col>
        <Col xs={12} lg={6}><Card className="contact-kpi net" bordered={false}><Statistic title="ส่วนต่างสุทธิ" value={Number(summary.net_value || 0)} precision={2} suffix="บาท" prefix={<SwapOutlined />} /><Text type="secondary">ยอดขายหักยอดซื้อ</Text></Card></Col>
        <Col xs={12} lg={6}><Card className="contact-kpi outstanding" bordered={false}><Statistic title="ลูกหนี้คงค้าง" value={Number(sales.outstanding_amount || 0)} precision={2} suffix="บาท" prefix={<WalletOutlined />} /><Text type="secondary">ยอดขายที่ยังรับชำระไม่ครบ</Text></Card></Col>
      </Row>

      <Row gutter={[12, 12]}>
        <Col xs={24} xl={16}><Card title={<Space><BankOutlined />แนวโน้มยอดซื้อ–ยอดขาย</Space>} bordered={false}>{chartData.length ? <Column {...chartConfig} /> : <Empty description="ยังไม่มีรายการในช่วงเวลานี้" />}</Card></Col>
        <Col xs={24} xl={8}><Card title="รายละเอียดมูลค่า" bordered={false} className="contact-dashboard__breakdown">
          <div><Text>ยอดขายก่อน VAT</Text><Text strong>{money(sales.subtotal)} บาท</Text></div>
          <div><Text>ภาษีขาย</Text><Text strong>{money(sales.tax_amount)} บาท</Text></div>
          <div><Text>รับชำระแล้ว</Text><Text strong className="positive">{money(sales.paid_amount)} บาท</Text></div>
          <div><Text>ยอดซื้อก่อน VAT</Text><Text strong>{money(purchases.subtotal)} บาท</Text></div>
          <div><Text>ภาษีซื้อ</Text><Text strong>{money(purchases.tax_amount)} บาท</Text></div>
          <div><Text>ค่าใช้จ่ายรอดำเนินการ</Text><Text strong className="warning">{money(purchases.pending_amount)} บาท</Text></div>
          <div><Text>เอกสารลงบัญชีแล้ว</Text><Text strong>{money(Number(sales.posted_amount || 0) + Number(purchases.posted_amount || 0))} บาท</Text></div>
        </Card></Col>
      </Row>

      <Card
        className="contact-dashboard__transactions"
        title={<Space><FileTextOutlined /><span>ประวัติการทำรายการ</span></Space>}
        extra={<Select value={transactionType} onChange={value => { setTransactionType(value); setPage(1) }} style={{ width: isMobile ? 125 : 160 }} options={[{ value: 'all', label: 'ทั้งหมด' }, { value: 'sale', label: 'เฉพาะยอดขาย' }, { value: 'purchase', label: 'เฉพาะยอดซื้อ' }]} />}
        bordered={false}
      >
        {transactions.length ? (isMobile
          ? <div className="contact-dashboard__mobile-list">{transactions.map(renderMobileTransaction)}</div>
          : <Table rowKey={row => `${row.transaction_type}-${row.id}`} columns={columns} dataSource={transactions} pagination={false} loading={loading} scroll={{ x: 900 }} />)
          : <Empty description="ยังไม่มีประวัติการทำรายการ" />}
        {pagination.total > pagination.limit && <Pagination current={page} pageSize={pagination.limit} total={pagination.total} showSizeChanger={false} onChange={setPage} />}
      </Card>
    </>}
  </div>
}
